import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  AccessibilityHelp,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BalloonToolbar,
  BlockQuote,
  Bold,
  Code,
  CodeBlock,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  FullPage,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  HtmlComment,
  HtmlEmbed,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  SelectAll,
  ShowBlocks,
  SimpleUploadAdapter,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline,
  Undo
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminCreateArticle() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Le Marketing de réseau et le NetWorking',
    image_url: '',
    source_url: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Check auth
    if (!sessionStorage.getItem('adminAuth')) {
      navigate('/admin');
      return;
    }
  }, [navigate]);

  // Auto-generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setFormData({
      ...formData,
      title: newTitle,
      slug: generateSlug(newTitle)
    });
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.title || !formData.slug || !formData.excerpt || !formData.category) {
      alert('❌ Veuillez remplir tous les champs obligatoires (titre, slug, extrait, catégorie)');
      return;
    }

    setSaving(true);
    console.log('🔍 Creating new article:', {
      endpoint: `${API}/articles`,
      data: formData
    });

    try {
      const response = await axios.post(`${API}/articles`, formData);
      console.log('✅ Article created successfully:', response.data);
      alert('✅ Article créé avec succès !');
      navigate('/admin/articles');
    } catch (error) {
      console.error('❌ Error creating article:', error);
      console.error('Error response:', error.response?.data);
      alert(`❌ Erreur lors de la création: ${error.response?.data?.detail || error.message}`);
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/articles');
  };

  return (
    <div className="app">
      <nav className="nav">
        <div className="nav-container">
          <div className="nav-logo">
            <span className="logo-text">AmsWeb Admin</span>
          </div>
          <div className="nav-links">
            <button onClick={() => navigate('/admin/articles')} className="btn btn-secondary">
              ← Dashboard
            </button>
          </div>
        </div>
      </nav>

      <section className="admin-edit-section">
        <div className="container">
          <h1>➕ Créer un Nouvel Article</h1>
          
          <div className="admin-edit-form">
            <div className="form-group">
              <label>Titre : <span style={{color: 'red'}}>*</span></label>
              <input
                type="text"
                value={formData.title}
                onChange={handleTitleChange}
                className="admin-form-input"
                placeholder="Entrez le titre de l'article"
              />
            </div>

            <div className="form-group">
              <label>Slug (URL) : <span style={{color: 'red'}}>*</span></label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({...formData, slug: e.target.value})}
                className="admin-form-input"
                placeholder="slug-de-larticle"
              />
              <small>Généré automatiquement à partir du titre. Vous pouvez le modifier.</small>
            </div>

            <div className="form-group">
              <label>Catégorie : <span style={{color: 'red'}}>*</span></label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="admin-form-select"
              >
                <option>Le Marketing de réseau et le NetWorking</option>
                <option>Les outils pour bien démarrer</option>
                <option>Découvrir les nouveaux MLM</option>
              </select>
            </div>

            <div className="form-group">
              <label>Image URL :</label>
              <input
                type="text"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                className="admin-form-input"
                placeholder="/images/blog/mon-image.jpg"
              />
              {formData.image_url && (
                <div className="image-preview">
                  <img src={formData.image_url} alt="Preview" />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Extrait : <span style={{color: 'red'}}>*</span></label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                className="admin-form-textarea"
                rows="3"
                placeholder="Un court extrait qui s'affichera sur la page du blog"
              />
            </div>

            <div className="form-group">
              <label>Contenu Complet :</label>
              
              <div style={{ marginTop: '1rem' }}>
                <CKEditor
                  editor={ClassicEditor}
                  data={formData.content}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setFormData({...formData, content: data});
                  }}
                  config={{
                    licenseKey: 'GPL',
                    toolbar: {
                      items: [
                        'undo', 'redo',
                        '|',
                        'sourceEditing', 'showBlocks',
                        '|',
                        'heading',
                        '|',
                        'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor',
                        '|',
                        'bold', 'italic', 'underline', 'strikethrough', 'subscript', 'superscript', 'code', 'removeFormat',
                        '|',
                        'link', 'insertImage', 'mediaEmbed', 'insertTable', 'highlight', 'blockQuote', 'codeBlock', 'htmlEmbed',
                        '|',
                        'bulletedList', 'numberedList', 'todoList', 'outdent', 'indent'
                      ],
                      shouldNotGroupWhenFull: false
                    },
                    plugins: [
                      AccessibilityHelp, Autoformat, AutoImage, AutoLink, Autosave, BalloonToolbar, 
                      BlockQuote, Bold, Code, CodeBlock, Essentials, FindAndReplace, FontBackgroundColor, 
                      FontColor, FontFamily, FontSize, FullPage, GeneralHtmlSupport, Heading, Highlight, 
                      HorizontalLine, HtmlComment, HtmlEmbed, ImageBlock, ImageCaption, ImageInline, 
                      ImageInsert, ImageInsertViaUrl, ImageResize, ImageStyle, ImageTextAlternative, 
                      ImageToolbar, ImageUpload, Indent, IndentBlock, Italic, Link, LinkImage, List, 
                      ListProperties, MediaEmbed, Paragraph, PasteFromOffice, RemoveFormat, SelectAll, 
                      ShowBlocks, SourceEditing, SpecialCharacters, SpecialCharactersArrows, 
                      SpecialCharactersCurrency, SpecialCharactersEssentials, SpecialCharactersLatin, 
                      SpecialCharactersMathematical, SpecialCharactersText, Strikethrough, Style, 
                      Subscript, Superscript, Table, TableCaption, TableCellProperties, TableColumnResize, 
                      TableProperties, TableToolbar, TextTransformation, TodoList, Underline, Undo
                    ],
                    balloonToolbar: ['bold', 'italic', '|', 'link', '|', 'bulletedList', 'numberedList'],
                    fontFamily: {
                      supportAllValues: true
                    },
                    fontSize: {
                      options: [10, 12, 14, 'default', 18, 20, 22, 24, 26, 28, 36, 48, 72],
                      supportAllValues: true
                    },
                    heading: {
                      options: [
                        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                        { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
                        { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
                        { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
                      ]
                    },
                    htmlSupport: {
                      allow: [
                        {
                          name: /^.*$/,
                          styles: true,
                          attributes: true,
                          classes: true
                        }
                      ]
                    },
                    image: {
                      toolbar: [
                        'toggleImageCaption', 'imageTextAlternative', '|',
                        'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|',
                        'resizeImage'
                      ]
                    },
                    link: {
                      addTargetToExternalLinks: true,
                      defaultProtocol: 'https://',
                      decorators: {
                        toggleDownloadable: {
                          mode: 'manual',
                          label: 'Downloadable',
                          attributes: {
                            download: 'file'
                          }
                        }
                      }
                    },
                    list: {
                      properties: {
                        styles: true,
                        startIndex: true,
                        reversed: true
                      }
                    },
                    menuBar: {
                      isVisible: true
                    },
                    placeholder: 'Commencez à écrire votre article ici...',
                    table: {
                      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
                    }
                  }}
                />
              </div>
            </div>

            <div className="form-group">
              <label>URL Source (optionnel) :</label>
              <input
                type="text"
                value={formData.source_url}
                onChange={(e) => setFormData({...formData, source_url: e.target.value})}
                className="admin-form-input"
                placeholder="https://..."
              />
              <small>Si l'article provient d'une autre source</small>
            </div>

            <div className="admin-edit-buttons">
              <button 
                onClick={handleSubmit} 
                className="btn btn-primary btn-large"
                disabled={saving}
              >
                {saving ? 'Création...' : '✅ Créer l\'Article'}
              </button>
              <button 
                onClick={handleCancel} 
                className="btn btn-secondary btn-large"
                disabled={saving}
              >
                ❌ Annuler
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AdminCreateArticle;
